import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  CreditCard,
  KeyRound,
  Loader2,
  MailPlus,
  RefreshCw,
  ShieldAlert,
  Sliders,
  TrendingUp,
  TriangleAlert,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { formatarPreco } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  api,
  type EventoSeguranca,
  type LeadFunil,
  type ResumoDaRecuperacao,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { BibliotecaDoPainel } from './BibliotecaDoPainel';
import { FluxoDoPainel } from './FluxoDoPainel';
import { PlanosDoPainel } from './PlanosDoPainel';
import { RecuperacaoDeVendas } from './RecuperacaoDeVendas';
import { UsuariosDoPainel } from './UsuariosDoPainel';

const ROTULO_STATUS: Record<LeadFunil['status'], string> = {
  SOLICITADO: 'Pediu o link',
  CONFIRMADO: 'Validou o e-mail',
  CONVERTIDO: 'Assinou',
  ABANDONADO: 'Arquivado',
};

const TOM_STATUS: Record<LeadFunil['status'], 'principal' | 'destaque' | 'neutro'> = {
  SOLICITADO: 'destaque',
  CONFIRMADO: 'principal',
  CONVERTIDO: 'principal',
  ABANDONADO: 'neutro',
};

const ROTULO_EVENTO: Record<string, string> = {
  SQL_INJECTION: 'Tentativa de injeção SQL',
  PATH_TRAVERSAL: 'Tentativa de ler arquivo do servidor',
  XSS: 'Tentativa de script',
  RATE_LIMIT: 'Excesso de requisições',
  LOGIN_FALHO: 'Senha incorreta',
  BLOQUEIO_LOGIN: 'Acesso bloqueado por tentativas',
  ACESSO_NEGADO: 'Acesso negado',
  UPLOAD_INVALIDO: 'Upload recusado',
  DEMO_ABUSO: 'Uso indevido da demonstração',
  PAYLOAD_GRANDE: 'Conteúdo grande demais',
  DADOS_DA_CONTA: 'Dados de contato alterados',
};

function quando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/* ------------------------------------------------------------------ */
/* Seções do painel                                                    */
/* ------------------------------------------------------------------ */

interface DefinicaoDeSecao {
  id: string;
  rotulo: string;
  descricao: string;
  icone: LucideIcon;
}

/**
 * As seções do painel, na ordem em que aparecem.
 *
 * Cada uma é uma área de trabalho inteira e independente (a de usuários sozinha
 * já é uma tabela grande). Empilhadas numa página só, o painel virava uma
 * rolagem longa em que ninguém achava nada — por isso agora só uma fica aberta
 * por vez, e a escolhida vai na URL (`?secao=`), o que sobrevive ao recarregar
 * e permite mandar o link direto para a seção.
 */
const SECOES: DefinicaoDeSecao[] = [
  {
    id: 'usuarios',
    rotulo: 'Usuários e acessos',
    descricao: 'Plano, validade, bloqueio e bônus de cada conta',
    icone: KeyRound,
  },
  {
    id: 'planos',
    rotulo: 'Planos do site',
    descricao: 'Preço, textos, limites e vitrine',
    icone: CreditCard,
  },
  {
    id: 'fluxo',
    rotulo: 'Fluxo de criação',
    descricao: 'Passos do assistente e recursos extras',
    icone: Sliders,
  },
  {
    id: 'funil',
    rotulo: 'Funil da demonstração',
    descricao: 'Quem pediu, quem testou e quem não converteu',
    icone: Users,
  },
  {
    id: 'recuperacao',
    rotulo: 'Recuperação de vendas',
    descricao: 'Carrinhos parados e demonstrações sem assinatura, com o caminho para retomar',
    icone: TrendingUp,
  },
  {
    id: 'biblioteca',
    rotulo: 'Biblioteca de trabalhos',
    descricao: 'Aprovar publicações, liberar sem cobrança e fechar o repasse com os autores',
    icone: BookOpen,
  },
  {
    id: 'seguranca',
    rotulo: 'Segurança',
    descricao: 'Bloqueios e tentativas de invasão',
    icone: ShieldAlert,
  },
];

const IDS_DAS_SECOES = SECOES.map((secao) => secao.id);

/* ------------------------------------------------------------------ */
/* Peças reutilizadas                                                  */
/* ------------------------------------------------------------------ */

/** Número em destaque no topo do painel. */
function Indicador({
  icone: Icone,
  rotulo,
  valor,
  detalhe,
}: {
  icone: LucideIcon;
  rotulo: string;
  valor: string | number;
  detalhe?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icone className="size-3.5" />
        {rotulo}
      </span>
      <span className="mt-1 block text-xl font-semibold tracking-tight">{valor}</span>
      {detalhe ? <span className="mt-0.5 block text-[11px] text-muted-foreground">{detalhe}</span> : null}
    </div>
  );
}

/** Título de uma seção aberta, com espaço para um selo à direita. */
function TituloDaSecao({ children, selo }: { children: React.ReactNode; selo?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="text-sm font-semibold">{children}</h2>
      {selo}
    </div>
  );
}

/** Bloco vazio, com a explicação do que apareceria ali. */
function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * Ações de um contato do funil.
 *
 * Fica separado porque a mesma linha aparece duas vezes: na tabela (telas
 * largas) e no cartão (celular).
 */
function AcoesDoLead({
  lead,
  trabalhando,
  aoLembrete,
  aoMudarStatus,
}: {
  lead: LeadFunil;
  trabalhando: boolean;
  aoLembrete: (lead: LeadFunil) => void;
  aoMudarStatus: (lead: LeadFunil, status: LeadFunil['status']) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button variant="contorno" tamanho="pequeno" onClick={() => aoLembrete(lead)} disabled={trabalhando}>
        <MailPlus />
        Lembrete
      </Button>
      {lead.status !== 'CONVERTIDO' ? (
        <Button
          variant="fantasma"
          tamanho="pequeno"
          onClick={() => aoMudarStatus(lead, 'CONVERTIDO')}
          disabled={trabalhando}
        >
          Assinou
        </Button>
      ) : null}
      {lead.status !== 'ABANDONADO' ? (
        <Button
          variant="fantasma"
          tamanho="pequeno"
          onClick={() => aoMudarStatus(lead, 'ABANDONADO')}
          disabled={trabalhando}
        >
          Arquivar
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Painel                                                              */
/* ------------------------------------------------------------------ */

/**
 * Painel do administrador único do sistema.
 *
 * Reúne o que o dono do produto precisa operar: contas e acessos, catálogo de
 * planos, fluxo de criação, funil da demonstração e o registro de segurança. A
 * faixa de indicadores mostra, sem abrir nada, onde está a atenção do dia.
 */
export function Admin() {
  const [leads, setLeads] = useState<LeadFunil[]>([]);
  const [resumo, setResumo] = useState<Record<string, number>>({});
  const [eventos, setEventos] = useState<EventoSeguranca[]>([]);
  const [bloqueios, setBloqueios] = useState(0);
  const [resumoSeguranca, setResumoSeguranca] = useState<Array<{ tipo: string; total: number }>>([]);
  const [recuperacao, setRecuperacao] = useState<ResumoDaRecuperacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [trabalhando, setTrabalhando] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  const [parametros, setParametros] = useSearchParams();
  const secaoDaUrl = parametros.get('secao');
  const secao = secaoDaUrl && IDS_DAS_SECOES.includes(secaoDaUrl) ? secaoDaUrl : 'usuarios';

  /**
   * As seções visitadas continuam montadas (só escondidas). Trocar de aba fica
   * instantâneo e não refaz as consultas que cada uma já tinha carregado.
   */
  const [visitadas, setVisitadas] = useState<string[]>([secao]);
  useEffect(() => {
    setVisitadas((lista) => (lista.includes(secao) ? lista : [...lista, secao]));
  }, [secao]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [funil, seguranca, vendas] = await Promise.all([
        api.funil({ busca: busca.trim() || undefined, limite: 100 }),
        api.eventosDeSeguranca(40),
        api.recuperacao(),
      ]);
      setLeads(funil.leads);
      setResumo(funil.resumo);
      setEventos(seguranca.eventos);
      setBloqueios(seguranca.bloqueiosAtivos);
      setResumoSeguranca(seguranca.resumo);
      setRecuperacao(vendas.resumo);
    } catch (falha) {
      setErro(
        falha instanceof Error
          ? falha.message
          : 'Não foi possível carregar o painel. Entre novamente com a conta de administrador.',
      );
    } finally {
      setCarregando(false);
    }
  }, [busca]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const enviarLembrete = async (lead: LeadFunil) => {
    setTrabalhando(lead.id);
    try {
      await api.enviarLembrete(lead.id);
      await carregar();
    } catch {
      setErro('Não foi possível enviar o lembrete agora.');
    } finally {
      setTrabalhando(null);
    }
  };

  const mudarStatus = async (lead: LeadFunil, status: LeadFunil['status']) => {
    setTrabalhando(lead.id);
    try {
      await api.atualizarLead(lead.id, status);
      await carregar();
    } catch {
      setErro('Não foi possível atualizar o contato.');
    } finally {
      setTrabalhando(null);
    }
  };

  const totalDoFunil = Object.values(resumo).reduce((soma, valor) => soma + valor, 0);
  const assinaram = resumo.CONVERTIDO ?? 0;
  const secaoAtual = SECOES.find((item) => item.id === secao);
  const consultasProntas = !carregando || leads.length > 0 || eventos.length > 0;

  return (
    <div className="space-y-6 py-2">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Painel do administrador</h1>
          <p className="text-sm text-muted-foreground">
            Contas e acessos, catálogo de planos, fluxo de criação, funil da demonstração e segurança do
            sistema. Área restrita ao administrador.
          </p>
        </div>
        <Button variant="contorno" tamanho="pequeno" onClick={carregar} disabled={carregando}>
          {carregando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Atualizar
        </Button>
      </header>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {/* Indicadores: o estado do dia sem precisar abrir seção nenhuma. */}
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
        <Indicador
          icone={Users}
          rotulo="Contatos no funil"
          valor={consultasProntas ? totalDoFunil : '—'}
          detalhe={
            consultasProntas
              ? `${resumo.SOLICITADO ?? 0} pediram o link · ${resumo.CONFIRMADO ?? 0} validaram`
              : undefined
          }
        />
        <Indicador
          icone={CreditCard}
          rotulo="Assinaram"
          valor={consultasProntas ? assinaram : '—'}
          detalhe={consultasProntas ? `${resumo.ABANDONADO ?? 0} arquivado(s)` : undefined}
        />
        <Indicador
          icone={ShieldAlert}
          rotulo="Bloqueios ativos"
          valor={consultasProntas ? bloqueios : '—'}
          detalhe="Tentativas repetidas de acesso, por e-mail e por IP"
        />
        <Indicador
          icone={TrendingUp}
          rotulo="Valor parado"
          valor={recuperacao ? formatarPreco(recuperacao.valorParadoCentavos) : '—'}
          detalhe={
            recuperacao
              ? `${recuperacao.carrinhos} carrinho(s) · ${recuperacao.demonstracoes} demonstração(ões) sem assinar`
              : undefined
          }
        />
      </div>

      {/* Navegação entre as seções */}
      <nav
        aria-label="Seções do painel"
        className="flex gap-1 overflow-x-auto rounded-lg border border-border bg-card p-1 lg:flex-wrap lg:overflow-visible"
      >
        {SECOES.map((item) => {
          const Icone = item.icone;
          const ativo = item.id === secao;
          const selo =
            item.id === 'funil' && totalDoFunil > 0
              ? totalDoFunil
              : item.id === 'seguranca' && bloqueios > 0
                ? bloqueios
                : item.id === 'recuperacao' && recuperacao
                  ? recuperacao.carrinhos + recuperacao.demonstracoes
                  : null;

          return (
            <button
              key={item.id}
              type="button"
              aria-current={ativo ? 'true' : undefined}
              onClick={() => setParametros({ secao: item.id })}
              title={item.descricao}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors sm:text-sm',
                ativo ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary',
              )}
            >
              <Icone className="size-4" />
              {item.rotulo}
              {selo !== null ? (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                    ativo ? 'bg-primary-foreground/20' : 'bg-secondary text-foreground',
                  )}
                >
                  {selo}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {secaoAtual ? (
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">{secaoAtual.rotulo}</strong> · {secaoAtual.descricao}
        </p>
      ) : null}

      {/* ---------------------------------------------------------- */}
      {/* Seções — só a visitada fica montada, a ativa fica visível   */}
      {/* ---------------------------------------------------------- */}
      <div className="space-y-6">
        {visitadas.includes('usuarios') ? (
          <div className={secao === 'usuarios' ? undefined : 'hidden'}>
            <UsuariosDoPainel />
          </div>
        ) : null}

        {visitadas.includes('planos') ? (
          <div className={secao === 'planos' ? undefined : 'hidden'}>
            <PlanosDoPainel />
          </div>
        ) : null}

        {visitadas.includes('fluxo') ? (
          <div className={secao === 'fluxo' ? undefined : 'hidden'}>
            <FluxoDoPainel />
          </div>
        ) : null}

        {visitadas.includes('biblioteca') ? (
          <div className={secao === 'biblioteca' ? undefined : 'hidden'}>
            <BibliotecaDoPainel />
          </div>
        ) : null}

        {visitadas.includes('funil') ? (
          <section className={cn('space-y-4', secao === 'funil' ? undefined : 'hidden')}>
            <div className="flex flex-wrap items-center gap-3">
              <TituloDaSecao
                selo={
                  <div className="flex flex-wrap gap-2">
                    {(['SOLICITADO', 'CONFIRMADO', 'CONVERTIDO', 'ABANDONADO'] as const).map((status) => (
                      <Badge key={status} tom={TOM_STATUS[status]}>
                        {ROTULO_STATUS[status]}: {resumo[status] ?? 0}
                      </Badge>
                    ))}
                  </div>
                }
              >
                Funil da demonstração
              </TituloDaSecao>
              <div className="relative ml-auto w-full sm:w-72">
                <input
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                  placeholder="Buscar por nome ou e-mail"
                  aria-label="Buscar contatos do funil"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm sm:h-9"
                />
              </div>
            </div>

            {leads.length === 0 ? (
              <Vazio>
                Nenhum contato ainda. Quem pedir a demonstração em <strong>/demo</strong> aparece aqui.
              </Vazio>
            ) : (
              <>
                {/* Celular: um cartão por contato, sem rolagem lateral. */}
                <ul className="grid gap-3 md:hidden">
                  {leads.map((lead) => (
                    <li key={lead.id} className="rounded-lg border border-border bg-card p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="block truncate font-medium text-foreground">{lead.nome || '—'}</span>
                          <span className="block truncate text-xs text-muted-foreground">{lead.email}</span>
                        </div>
                        <Badge tom={TOM_STATUS[lead.status]}>{ROTULO_STATUS[lead.status]}</Badge>
                      </div>

                      <dl className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                        <div className="flex gap-1">
                          <dt>Pedido em</dt>
                          <dd className="ml-auto text-right">{quando(lead.criadoEm)}</dd>
                        </div>
                        <div className="flex gap-1">
                          <dt>Último envio</dt>
                          <dd className="ml-auto text-right">
                            {lead.ultimoEnvioEm ? quando(lead.ultimoEnvioEm) : '—'}
                          </dd>
                        </div>
                        <div className="flex gap-1">
                          <dt>Uso</dt>
                          <dd className="ml-auto text-right">
                            {lead.acessosUsados} acesso(s) · {lead.tentativas} pedido(s)
                          </dd>
                        </div>
                      </dl>

                      <div className="mt-3">
                        <AcoesDoLead
                          lead={lead}
                          trabalhando={trabalhando === lead.id}
                          aoLembrete={enviarLembrete}
                          aoMudarStatus={mudarStatus}
                        />
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Telas largas: a tabela, que compara melhor as linhas. */}
                <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Contato</th>
                        <th className="px-3 py-2 font-semibold">Situação</th>
                        <th className="px-3 py-2 font-semibold">Pedido</th>
                        <th className="px-3 py-2 font-semibold">Último envio</th>
                        <th className="px-3 py-2 font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id} className="border-t border-border/70 align-top">
                          <td className="px-3 py-2">
                            <span className="block font-medium text-foreground">{lead.nome || '—'}</span>
                            <span className="text-muted-foreground">{lead.email}</span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge tom={TOM_STATUS[lead.status]}>{ROTULO_STATUS[lead.status]}</Badge>
                            <span className="mt-1 block text-[11px] text-muted-foreground">
                              {lead.acessosUsados} acesso(s) · {lead.tentativas} pedido(s)
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{quando(lead.criadoEm)}</td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {lead.ultimoEnvioEm ? quando(lead.ultimoEnvioEm) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <AcoesDoLead
                              lead={lead}
                              trabalhando={trabalhando === lead.id}
                              aoLembrete={enviarLembrete}
                              aoMudarStatus={mudarStatus}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        ) : null}

        {visitadas.includes('recuperacao') ? (
          <div className={secao === 'recuperacao' ? undefined : 'hidden'}>
            <RecuperacaoDeVendas />
          </div>
        ) : null}

        {visitadas.includes('seguranca') ? (
          <section className={cn('space-y-4', secao === 'seguranca' ? undefined : 'hidden')}>
            <div className="flex flex-wrap items-center gap-3">
              <TituloDaSecao
                selo={
                  <>
                    <Badge tom={bloqueios > 0 ? 'destaque' : 'neutro'}>{bloqueios} bloqueio(s) ativo(s)</Badge>
                    <div className="flex flex-wrap gap-2">
                      {resumoSeguranca.map((linha) => (
                        <Badge key={linha.tipo} className="text-[10px]">
                          {ROTULO_EVENTO[linha.tipo] ?? linha.tipo}: {linha.total}
                        </Badge>
                      ))}
                    </div>
                  </>
                }
              >
                Segurança
              </TituloDaSecao>
            </div>

            {eventos.length === 0 ? (
              <Vazio>Nenhuma tentativa suspeita registrada até agora.</Vazio>
            ) : (
              <>
                {/* Celular: cartões, com o detalhe em texto corrido. */}
                <ul className="grid gap-3 md:hidden">
                  {eventos.map((evento) => (
                    <li key={evento.id} className="rounded-lg border border-border bg-card p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <span className="font-medium text-foreground">
                          {ROTULO_EVENTO[evento.tipo] ?? evento.tipo}
                        </span>
                        <Badge
                          tom={
                            evento.severidade === 'ALTA'
                              ? 'destaque'
                              : evento.severidade === 'MEDIA'
                                ? 'atencao'
                                : 'neutro'
                          }
                        >
                          {evento.severidade}
                        </Badge>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">{quando(evento.criadoEm)}</p>
                      {evento.detalhe ? (
                        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{evento.detalhe}</p>
                      ) : null}
                      <p className="mt-1.5 font-mono text-[11px] text-muted-foreground">
                        {evento.ip ?? '—'}
                        {evento.rota ? ` · ${evento.rota}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>

                <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Quando</th>
                        <th className="px-3 py-2 font-semibold">Evento</th>
                        <th className="px-3 py-2 font-semibold">Origem</th>
                        <th className="px-3 py-2 font-semibold">Detalhe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventos.map((evento) => (
                        <tr key={evento.id} className="border-t border-border/70 align-top">
                          <td className="px-3 py-2 text-muted-foreground">{quando(evento.criadoEm)}</td>
                          <td className="px-3 py-2">
                            <span className="font-medium text-foreground">
                              {ROTULO_EVENTO[evento.tipo] ?? evento.tipo}
                            </span>
                            <span className="ml-1 text-[10px] uppercase text-muted-foreground">
                              {evento.severidade}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {evento.ip ?? '—'}
                            {evento.rota ? <span className="block text-[11px]">{evento.rota}</span> : null}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{evento.detalhe ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
